import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Banknote,
  CheckCircle2,
  ChevronRight,
  Clock,
  MapPin,
  MessageCircle,
  Navigation,
  Phone,
  RefreshCw,
  Wallet,
  XCircle,
} from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { clockTime, formatMinutes, minutesBetween, promiseLabel, promiseState, type Urgency } from '@/lib/onlineOrderTiming'
import { amountDue, navigateUrl, telUrl, whatsAppUrl } from '@/lib/riderLinks'
import type { RiderMe, RiderTicket } from '@/types/rider'
import { RiderCollectDialog } from '@/components/rider/RiderCollectDialog'
import { RiderOutcomeDialog } from '@/components/rider/RiderOutcomeDialog'
import { RiderCashSheet } from '@/components/rider/RiderCashSheet'
import { RiderLiveStrip } from '@/components/rider/RiderLiveStrip'
import { useRiderLocationSharing } from '@/hooks/useRiderLocationSharing'

// Delivery riders (F-DLV-N1). A rider's whole app: their queue (most urgent
// first), one ticket at a time, and the money they hold. Phone-first, but the
// same Button / text-sm scale as the rest of the POS.

export const RIDER_ME_QUERY_KEY = ['rider', 'me'] as const

const URGENCY_TEXT: Record<Urgency, string> = {
  calm: 'text-muted-foreground',
  warn: 'text-amber-600 font-medium',
  late: 'text-red-600 font-semibold',
}

const STEP_LABEL: Record<string, string> = {
  assigned: 'To pick up',
  picked_up: 'On the way',
  arrived: 'At the door',
}

function useNow(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

/** "Placed 23m ago" + "12m left" / "6m late" against the promise. */
export function TicketClock({ ticket, now }: { ticket: RiderTicket; now: number }) {
  const s = promiseState(new Date(ticket.due_at).getTime(), now)
  const since = minutesBetween(new Date(ticket.placed_at).getTime(), now)
  return (
    <span className="inline-flex items-center gap-1 text-xs">
      <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      <span className={URGENCY_TEXT[s.urgency]} title={`Due ${clockTime(ticket.due_at)}`}>
        {promiseLabel(s)}
      </span>
      <span className="text-muted-foreground">· placed {formatMinutes(since)} ago</span>
    </span>
  )
}

export function RiderPage() {
  const qc = useQueryClient()
  const now = useNow()
  const { formatCurrency } = useCurrency()
  const [openId, setOpenId] = useState<string | null>(null)
  const [cashOpen, setCashOpen] = useState(false)

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: RIDER_ME_QUERY_KEY,
    queryFn: () => apiClient.getRiderMe(),
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  })
  const me = data?.data as RiderMe | undefined
  // F-DLV-N2: share the phone's position while on shift — kept running while
  // a ticket is open, since that is exactly when the rider is on the road.
  const location = useRiderLocationSharing(!!me?.on_shift)

  const shift = useMutation({
    mutationFn: (on: boolean) => apiClient.setRiderShift(on),
    onSuccess: () => qc.invalidateQueries({ queryKey: RIDER_ME_QUERY_KEY }),
    onError: (err: unknown) =>
      toastHelpers.error('Could not change shift', (err as { message?: string })?.message || 'Please try again.'),
  })

  const open = useMemo(() => me?.queue.find((t) => t.assignment_id === openId) ?? null, [me, openId])

  if (isLoading) {
    return <div className="p-6 text-center text-sm text-muted-foreground">Loading your deliveries…</div>
  }
  if (isError || !me) {
    return (
      <div className="flex flex-col items-center gap-3 p-8 text-center">
        <p className="text-sm text-muted-foreground">Couldn’t load your deliveries. Check your connection.</p>
        <Button variant="outline" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    )
  }

  if (open) {
    return (
      <RiderTicketView
        ticket={open}
        now={now}
        me={me}
        onBack={() => setOpenId(null)}
        formatCurrency={formatCurrency}
      />
    )
  }

  const heldTotal = me.held.cash + me.held.wallet

  return (
    <div className="mx-auto flex h-full w-full max-w-xl flex-col overflow-y-auto bg-background">
      <div className="sticky top-0 z-10 space-y-3 border-b bg-background/95 p-4 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold">My deliveries</h1>
            <p className="text-xs text-muted-foreground">
              {me.on_shift
                ? `On shift since ${clockTime(me.shift_started_at)}`
                : 'Off shift — the counter won’t see you as available'}
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className={me.on_shift ? 'font-medium text-emerald-600' : 'text-muted-foreground'}>
              {me.on_shift ? 'On shift' : 'Off'}
            </span>
            <Switch
              checked={me.on_shift}
              disabled={shift.isPending}
              onCheckedChange={(v) => shift.mutate(v)}
              aria-label="On shift"
            />
          </label>
        </div>
        <RiderLiveStrip onShift={me.on_shift} location={location} now={now} />
        <button
          type="button"
          onClick={() => setCashOpen(true)}
          className="flex w-full items-center justify-between rounded-lg border bg-card px-3 py-2 text-left text-sm hover:bg-muted"
        >
          <span className="inline-flex items-center gap-2">
            <Wallet className="h-4 w-4 text-muted-foreground" aria-hidden />
            {heldTotal > 0 ? (
              <span>
                You hold <span className="font-semibold">{formatCurrency(heldTotal)}</span>
                {me.held.wallet > 0 && (
                  <span className="text-muted-foreground"> ({formatCurrency(me.held.wallet)} in your wallet)</span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground">You hold no money</span>
            )}
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
        </button>
      </div>

      <div className="flex-1 space-y-2.5 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">
            Queue <span className="font-normal text-muted-foreground">({me.queue.length})</span>
          </h2>
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
            Refresh
          </Button>
        </div>
        {me.queue.length === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            Nothing assigned to you right now.
          </p>
        ) : (
          me.queue.map((t, i) => (
            <button
              key={t.assignment_id}
              type="button"
              onClick={() => setOpenId(t.assignment_id)}
              className="block w-full rounded-lg border bg-card p-3 text-left shadow-sm hover:bg-muted/50"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
                    {i + 1}
                  </span>
                  <span className="text-sm font-semibold">#{t.order_number}</span>
                  <Badge variant="secondary" className="text-[10px]">
                    {STEP_LABEL[t.assignment_status ?? ''] ?? t.assignment_status}
                  </Badge>
                </div>
                <PayChip ticket={t} formatCurrency={formatCurrency} />
              </div>
              <p className="mt-1.5 text-sm font-medium">{t.customer_name || 'Customer'}</p>
              <p className="line-clamp-2 text-sm text-muted-foreground">{t.delivery_address || 'No address on the order'}</p>
              <div className="mt-1.5">
                <TicketClock ticket={t} now={now} />
              </div>
            </button>
          ))
        )}

        {me.done.length > 0 && (
          <details className="pt-4">
            <summary className="cursor-pointer text-sm font-semibold">
              Finished today <span className="font-normal text-muted-foreground">({me.done.length})</span>
            </summary>
            <ul className="mt-2 divide-y rounded-lg border bg-card text-sm">
              {me.done.map((t) => (
                <li key={t.assignment_id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span className="truncate">
                    #{t.order_number} · {t.customer_name || 'Customer'}
                  </span>
                  {t.assignment_status === 'delivered' ? (
                    <span className="inline-flex shrink-0 items-center gap-1 text-xs text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> {clockTime(t.finished_at)}
                    </span>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 text-xs text-red-600">
                      <XCircle className="h-3.5 w-3.5" aria-hidden /> Returned
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      <RiderCashSheet open={cashOpen} onOpenChange={setCashOpen} />
    </div>
  )
}

function PayChip({ ticket, formatCurrency }: { ticket: RiderTicket; formatCurrency: (n: number) => string }) {
  const due = amountDue(ticket.total, ticket.paid)
  if (due <= 0) {
    return (
      <Badge variant="outline" className="shrink-0 border-emerald-600/40 text-[11px] text-emerald-700 dark:text-emerald-400">
        Paid
      </Badge>
    )
  }
  return (
    <span className="shrink-0 text-right text-xs">
      <span className="block text-muted-foreground">Collect</span>
      <span className="text-sm font-semibold">{formatCurrency(due)}</span>
    </span>
  )
}

function RiderTicketView({
  ticket,
  now,
  me,
  onBack,
  formatCurrency,
}: {
  ticket: RiderTicket
  now: number
  me: RiderMe
  onBack: () => void
  formatCurrency: (n: number) => string
}) {
  const qc = useQueryClient()
  const [collectOpen, setCollectOpen] = useState(false)
  const [outcome, setOutcome] = useState<'delivered' | 'failed' | null>(null)
  const due = amountDue(ticket.total, ticket.paid)
  const nav = navigateUrl(ticket.lat, ticket.lng, ticket.delivery_address)
  const tel = telUrl(ticket.customer_phone)
  const wa = whatsAppUrl(ticket.customer_phone)
  const step = ticket.assignment_status
  const onRoad = step === 'picked_up' || step === 'arrived'

  const progress = useMutation({
    mutationFn: (status: 'picked_up' | 'arrived') => apiClient.updateRiderAssignment(ticket.assignment_id!, { status }),
    onSuccess: (_d, status) => {
      toastHelpers.success(status === 'picked_up' ? 'Picked up — on the way' : 'Marked at the door')
      qc.invalidateQueries({ queryKey: RIDER_ME_QUERY_KEY })
    },
    onError: (err: unknown) =>
      toastHelpers.error('Could not update', (err as { message?: string })?.message || 'Please try again.'),
  })

  return (
    <div className="mx-auto flex h-full w-full max-w-xl flex-col overflow-y-auto bg-background">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-background/95 p-3 backdrop-blur">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" aria-hidden /> Queue
        </Button>
        <span className="text-sm font-semibold">#{ticket.order_number}</span>
        <Badge variant="secondary" className="text-[10px]">
          {STEP_LABEL[step ?? ''] ?? step}
        </Badge>
      </div>

      <div className="flex-1 space-y-4 p-4">
        <section className="space-y-2 rounded-lg border bg-card p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-semibold">{ticket.customer_name || 'Customer'}</p>
              {ticket.customer_phone && <p className="text-sm text-muted-foreground">{ticket.customer_phone}</p>}
            </div>
            <TicketClock ticket={ticket} now={now} />
          </div>
          <p className="flex items-start gap-1.5 text-sm">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>
              {ticket.delivery_address || 'No address on the order — call the customer'}
              {ticket.zone_name && <span className="block text-xs text-muted-foreground">{ticket.zone_name}</span>}
            </span>
          </p>
          {ticket.notes && (
            <p className="rounded-md bg-amber-50 px-2 py-1.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              {ticket.notes}
            </p>
          )}
          <div className="grid grid-cols-3 gap-2 pt-1">
            <Button asChild={!!nav} variant="default" disabled={!nav}>
              {nav ? (
                <a href={nav} target="_blank" rel="noreferrer">
                  <Navigation className="mr-1.5 h-4 w-4" aria-hidden /> Navigate
                </a>
              ) : (
                <span>Navigate</span>
              )}
            </Button>
            <Button asChild={!!tel} variant="outline" disabled={!tel}>
              {tel ? (
                <a href={tel}>
                  <Phone className="mr-1.5 h-4 w-4" aria-hidden /> Call
                </a>
              ) : (
                <span>Call</span>
              )}
            </Button>
            <Button asChild={!!wa} variant="outline" disabled={!wa}>
              {wa ? (
                <a href={wa} target="_blank" rel="noreferrer">
                  <MessageCircle className="mr-1.5 h-4 w-4" aria-hidden /> WhatsApp
                </a>
              ) : (
                <span>WhatsApp</span>
              )}
            </Button>
          </div>
        </section>

        <section className="rounded-lg border bg-card p-3">
          <h2 className="mb-2 text-sm font-semibold">Items</h2>
          <ul className="space-y-1.5 text-sm">
            {ticket.items.map((it, i) => (
              <li key={i}>
                <span className="font-medium">{it.quantity}×</span> {it.name}
                {it.modifier_labels.length > 0 && (
                  <span className="block pl-5 text-xs text-muted-foreground">{it.modifier_labels.join(', ')}</span>
                )}
                {it.special_instructions && (
                  <span className="block pl-5 text-xs text-amber-700 dark:text-amber-400">“{it.special_instructions}”</span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 border-t pt-2 text-xs text-muted-foreground">Placed {clockTime(ticket.placed_at)}</p>
        </section>

        <section className="rounded-lg border bg-card p-3 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Order total</span>
            <span>{formatCurrency(ticket.total)}</span>
          </div>
          {ticket.paid > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Already paid</span>
              <span>{formatCurrency(ticket.paid)}</span>
            </div>
          )}
          <div className="mt-1 flex justify-between border-t pt-1 font-semibold">
            <span>{due > 0 ? 'To collect' : 'Nothing to collect'}</span>
            <span>{due > 0 ? formatCurrency(due) : 'Paid'}</span>
          </div>
          {due > 0 && ticket.pay_intent && (
            <p className="mt-1 text-xs text-muted-foreground">
              Customer chose {ticket.pay_intent === 'cash' ? 'cash on delivery' : ticket.pay_intent === 'card' ? 'card at the door' : 'online transfer'}.
            </p>
          )}
        </section>
      </div>

      <div className="sticky bottom-0 space-y-2 border-t bg-background/95 p-4 backdrop-blur">
        {step === 'assigned' && (
          <Button className="w-full" onClick={() => progress.mutate('picked_up')} disabled={progress.isPending}>
            I’ve picked it up
          </Button>
        )}
        {onRoad && (
          <>
            {due > 0 && (
              <Button className="w-full" variant="outline" onClick={() => setCollectOpen(true)}>
                <Banknote className="mr-1.5 h-4 w-4" aria-hidden /> Collect {formatCurrency(due)}
              </Button>
            )}
            {step === 'picked_up' && (
              <Button className="w-full" variant="outline" onClick={() => progress.mutate('arrived')} disabled={progress.isPending}>
                I’m at the door
              </Button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => setOutcome('failed')}>
                Couldn’t deliver
              </Button>
              <Button onClick={() => setOutcome('delivered')}>Delivered</Button>
            </div>
          </>
        )}
      </div>

      <RiderCollectDialog
        open={collectOpen}
        onOpenChange={setCollectOpen}
        ticket={ticket}
        due={due}
        subMethods={me.online_submethods}
      />
      <RiderOutcomeDialog
        mode={outcome}
        onClose={(done) => {
          setOutcome(null)
          if (done) onBack()
        }}
        ticket={ticket}
        due={due}
      />
    </div>
  )
}

